import { useEffect, useState } from 'react';

// Brings `Intl.NumberFormat({ notation: 'compact' })` support to Safari.
import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/en';
import '@formatjs/intl-numberformat/locale-data/fi';
import '@formatjs/intl-numberformat/locale-data/et';
import '@formatjs/intl-numberformat/locale-data/lt';
import '@formatjs/intl-numberformat/locale-data/lv';
import '@formatjs/intl-numberformat/locale-data/sv';
import '@formatjs/intl-numberformat/locale-data/da';
import '@formatjs/intl-numberformat/locale-data/hu';
import '@formatjs/intl-numberformat/locale-data/ka';
import '@formatjs/intl-numberformat/locale-data/cs';
import '@formatjs/intl-numberformat/locale-data/nb'; // Norwegian Bokmål ("book tongue")
import '@formatjs/intl-numberformat/locale-data/he';
import '@formatjs/intl-numberformat/locale-data/el';
import '@formatjs/intl-numberformat/locale-data/hr';
import '@formatjs/intl-numberformat/locale-data/sr';
import '@formatjs/intl-numberformat/locale-data/pl';
import '@formatjs/intl-numberformat/locale-data/az';
import '@formatjs/intl-numberformat/locale-data/sl';
import '@formatjs/intl-numberformat/locale-data/ru';
import '@formatjs/intl-numberformat/locale-data/sk';
import '@formatjs/intl-numberformat/locale-data/ja';
import '@formatjs/intl-numberformat/locale-data/de';
import '@formatjs/intl-numberformat/locale-data/is';
import '@formatjs/intl-numberformat/locale-data/ar';
import '@formatjs/intl-numberformat/locale-data/ar-IL';
import '@formatjs/intl-numberformat/locale-data/fr';
import '@formatjs/intl-numberformat/locale-data/sq';
import '@formatjs/intl-numberformat/locale-data/uz';

import flattenObj from './flattenObj';
import messagesEn from './messages/en.json';

export const supportedUiLanguages = [
  'en',
  'fi',
  'ar',
  'ar-IL',
  'et',
  'lt',
  'lv',
  'sv',
  'da',
  'hu',
  'ka',
  'cs',
  'no',
  'he',
  'el',
  'hr',
  'sr',
  'pl',
  'az',
  'sl',
  'ru',
  'sk',
  'ja',
  'de',
  'is',
  'fr',
  'sq',
  'uz',
] as const;

export type UiLanguageKey = (typeof supportedUiLanguages)[number];

export const isSupportedUiLanguage = (languageKey: string): languageKey is UiLanguageKey => {
  return (supportedUiLanguages as readonly string[]).includes(languageKey);
};

export type LanguageChangeListener = (languageKey: UiLanguageKey) => void;

export type UiLanguage = {
  key: UiLanguageKey;
  label: string;
  rtl?: boolean;
};

export const defaultMessages = flattenObj(messagesEn, '.');

const languages: UiLanguage[] = [
  { key: 'en', label: 'English' },
  { key: 'fi', label: 'Suomi' },
  { key: 'ar', label: 'العربية', rtl: true },
  { key: 'ar-IL', label: 'العربية IL', rtl: true },
  { key: 'et', label: 'Eesti' },
  { key: 'lt', label: 'Lietuvių' },
  { key: 'lv', label: 'Latviešu' },
  { key: 'sv', label: 'Svenska' },
  { key: 'da', label: 'Dansk' },
  { key: 'hu', label: 'Magyar' },
  { key: 'ka', label: 'ქართული' }, // Georgian
  { key: 'cs', label: 'Čeština' },
  { key: 'no', label: 'Norsk' },
  { key: 'el', label: 'Ελληνικά' }, // Greek
  { key: 'he', label: 'עִברִית', rtl: true }, // Hebrew
  { key: 'hr', label: 'Hrvatski' },
  { key: 'pl', label: 'Polish' },
  { key: 'sr', label: 'Srpski' },
  { key: 'az', label: 'Azərbaycan' },
  { key: 'sl', label: 'Slovenski' },
  { key: 'ru', label: 'Русский' },
  { key: 'sk', label: 'Slovenský' },
  { key: 'ja', label: '日本語' },
  { key: 'de', label: 'Deutsch' },
  { key: 'is', label: 'Íslenska' },
  { key: 'fr', label: 'Français' },
  { key: 'sq', label: 'Shqip' },
  { key: 'uz', label: 'Oʻzbekcha' },
];

export const getAvailableLanguages = () => languages;

export const setLanguage = (languageKey: UiLanguageKey) => {
  try {
    localStorage.setItem('language', languageKey);
  } catch (e) {
    // no-op
  }

  languageChangeListeners.forEach(listener => listener(languageKey));
};

export const getLanguage = () => {
  let languageFromStorage = null;
  try {
    languageFromStorage = localStorage.getItem('language');
  } catch (e) {
    /**/
  }

  // iOS8 safari doesn't have navigator.languages
  const navigatorLanguages = navigator.languages || [navigator.language];

  const preferredLanguages = languageFromStorage
    ? [languageFromStorage].concat(navigatorLanguages)
    : navigatorLanguages;

  for (const locale of preferredLanguages) {
    // we have languages which specify the full locale, e.g. 'ar-IL', we check first if we have a full match
    const fullLanguageMatch = languages.find(lang => lang.key === locale);
    if (fullLanguageMatch) {
      return fullLanguageMatch;
    }

    // if we don't have a full match, we check if there's a language that matches ignoring locale, e.g. 'ar' for 'ar-IL'
    const languagePart = locale.substring(0, 2);
    const partialLanguageMatch = languages.find(lang => lang.key === languagePart);
    if (partialLanguageMatch) {
      return partialLanguageMatch;
    }
  }
  return languages[0];
};

const languageChangeListeners = new Set<LanguageChangeListener>();

export const addLanguageChangeListener = (listener: LanguageChangeListener) => {
  languageChangeListeners.add(listener);
};

export const removeLanguageChangeListener = (listener: LanguageChangeListener) => {
  languageChangeListeners.delete(listener);
};

export const useLanguage = () => {
  const [language, setLanguage] = useState(getLanguage());
  useEffect(() => {
    const refresh = () => {
      setLanguage(getLanguage());
    };
    addLanguageChangeListener(refresh);
    return () => {
      removeLanguageChangeListener(refresh);
    };
  });
  return language;
};
